import i18n from '@/i18n/config';

export interface EdgeFunctionFailure {
  /** HTTP status of the function's answer; undefined when the request never got one */
  status?: number;
  /** The `error` text from the function's JSON body, if it sent one */
  message?: string;
  /** No answer at all: offline, DNS, blocked */
  network: boolean;
}

const bodyMessage = (body: unknown): string | undefined => {
  if (typeof body === 'string') return body.trim() || undefined;
  if (body && typeof body === 'object') {
    const { error, message } = body as { error?: unknown; message?: unknown };
    if (typeof error === 'string') return error;
    if (typeof message === 'string') return message;
  }
  return undefined;
};

/**
 * supabase-js reduces every non-2xx answer to "Edge Function returned a non-2xx status code"
 * and keeps the real answer on error.context, the raw Response. Read the status and the
 * body's `error` text from there. `data` covers functions that answer 200 with an error body.
 */
export const readEdgeFunctionFailure = async (error: unknown, data?: unknown): Promise<EdgeFunctionFailure> => {
  const err = error as { name?: string; message?: string; context?: unknown } | null | undefined;
  const response = err?.context as Response | undefined;

  if (response && typeof response.clone === 'function') {
    let message: string | undefined;
    try {
      message = bodyMessage(await response.clone().json());
    } catch {
      // body is not JSON
    }
    return { status: response.status, message, network: false };
  }

  if (err?.name === 'FunctionsFetchError') return { network: true };

  return { message: bodyMessage(data), network: false };
};

export type OtpStep = 'send' | 'verify';

/**
 * Hebrew (or the page's language) text for a failed send-otp-consumer / verify-otp-consumer call.
 * The functions answer in English ('Invalid or expired OTP', 'Phone number already registered',
 * 'Failed to send SMS', ...); the visitor never sees those.
 */
export const otpErrorMessage = (failure: EdgeFunctionFailure, step: OtpStep): string => {
  const t = (key: string) => i18n.t(`auth.otpErrors.${key}`);
  const message = (failure.message || '').toLowerCase();

  if (failure.network) return t('network');
  if (/already registered/.test(message)) return i18n.t('auth.phoneAlreadyRegistered');
  if (/invalid or expired|invalid otp/.test(message)) return t('invalidCode');
  if (/invalid phone/.test(message)) return t('invalidPhone');
  if (/required/.test(message)) return t('missingFields');
  if (failure.status === 429) return t('tooManyAttempts');
  if (/failed to send sms/.test(message)) return t('sendFailed');
  if (failure.status !== undefined && failure.status >= 500) return t('serverError');
  if (step === 'verify' && (failure.status === undefined || failure.status === 400)) return t('invalidCode');
  return t(step === 'send' ? 'sendFailed' : 'generic');
};
